/**
 * HTTP API contract: typed route descriptors with Zod schemas. The backend mounts these descriptors
 * (kernel `mountRoute`) and the workbench/api-client reads the same objects. Paths use OpenAPI style
 * `{param}` placeholders; convert with `toHonoPath` for Hono. Non-2xx responses are always `ErrorBody`.
 */
import { z } from 'zod';
import { AutonomyClass, ChangePreview, LedgerEntry, SideEffectClass, ToolReceipt } from './actions.ts';
import { AgentDefinition } from './agent.ts';
import { Approval, ApprovalStatus } from './approval.ts';
import { Classification } from './artifact.ts';
import { AuditEntry } from './audit.ts';
import {
  EntityRef,
  Id,
  IdempotencyKey,
  IsoDateTime,
  JsonObject,
  JsonSchemaObject,
  SemVer,
  Sha256Hex,
} from './common.ts';
import { DecimalString } from './decimal.ts';
import { DemoAutoConfig, DemoMockBinding, DemoRunStatus, DemoSideEvent } from './demo.ts';
import { ResourceScope } from './entitlement.ts';
import { EpisodeTraceData, PlatformEvent } from './events.ts';
import { Evidence } from './evidence.ts';
import {
  AuthProvidersResponse,
  CreateDirectoryUserBody,
  DirectoryListQuery,
  DirectoryListResponse,
  DirectoryUser,
  SetDirectoryEnabledBody,
  SetDirectoryRolesBody,
} from './identity.ts';
import { KnowledgeSource } from './knowledge.ts';
import {
  ApprovalState,
  EffectiveModelProjection,
  ModelAvailability,
  ModelBinding,
  ModelCallRecord,
  ModelCapabilityAssertions,
  ModelCapabilityName,
  ModelCatalogEntry,
  ModelCatalogEntryId,
  ModelCatalogEntryInput,
  ModelConnectionId,
  ModelConnectionSummary,
  ModelCostAggregate,
  ModelCostCoverage,
  ModelCostStatus,
  ModelDefaultPointer,
  ModelManagementAudit,
  ModelManagementOperation,
  ModelManagementStatus,
  ModelOperation,
  ModelProfile,
  ModelProfilePointer,
  ModelProfileVersion,
  ModelProfileVersionRef,
  ModelRestriction,
  ModelSettings,
} from './model.ts';
import { ModuleManifest } from './module.ts';
import { PolicyBundle } from './policy.ts';
import { Principal } from './principal.ts';
import { Proposal } from './proposal.ts';
import { BundleEvalCompletedData, BundleOrigin, EvalProvenance, PolicyTestTable } from './studio.ts';
import { Task, TaskState, TimelineEntry } from './task.ts';
import { ToolCatalogEntry } from './tool.ts';
import { WorkflowRun, WorkflowStepRecord } from './workflow.ts';

// ---------------------------------------------------------------------------------------------
// Descriptor types
// ---------------------------------------------------------------------------------------------

export type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

export interface RouteRequest {
  params?: z.ZodType;
  query?: z.ZodType;
  body?: z.ZodType;
  /** Header names are lowercase. */
  headers?: z.ZodType;
}

export interface RouteDescriptor<Req extends RouteRequest = RouteRequest, Res extends z.ZodType = z.ZodType> {
  /** Stable id, `<group>.<name>`, also the OpenAPI operationId. */
  id: string;
  method: HttpMethod;
  /** OpenAPI-style path, e.g. `/tasks/{taskId}`. */
  path: string;
  summary: string;
  tags: readonly string[];
  request: Req;
  /** Success body schema. For `sse` routes: the schema of each event's `data` (a PlatformEvent). */
  response: Res;
  /** Success status; defaults to 200. */
  successStatus?: 200 | 201 | 202 | 204;
  /** Response encoding; defaults to `json`. */
  responseType?: 'json' | 'sse' | 'text';
  /** `public` routes need no session; default `session`. */
  auth?: 'public' | 'session';
  /** Principal roles allowed (any of). Absent = any authenticated principal. */
  roles?: readonly string[];
}

export type AnyRouteDescriptor = RouteDescriptor<RouteRequest, z.ZodType>;

/** Identity helper that keeps literal types of a descriptor. */
export function defineRoute<const Req extends RouteRequest, Res extends z.ZodType>(
  descriptor: RouteDescriptor<Req, Res>,
): RouteDescriptor<Req, Res> {
  return descriptor;
}

type InferOr<T, F> = T extends z.ZodType ? z.infer<T> : F;
type InputOr<T, F> = T extends z.ZodType ? z.input<T> : F;

/** Parsed (server-side) request parts of a descriptor. */
export interface RouteInput<D extends AnyRouteDescriptor> {
  params: InferOr<D['request']['params'], Record<string, never>>;
  query: InferOr<D['request']['query'], Record<string, never>>;
  body: InferOr<D['request']['body'], undefined>;
  headers: InferOr<D['request']['headers'], Record<string, never>>;
}

/** Client-side request parts (schema inputs, defaults optional). */
export interface RouteClientInput<D extends AnyRouteDescriptor> {
  params: InputOr<D['request']['params'], undefined>;
  query: InputOr<D['request']['query'], undefined>;
  body: InputOr<D['request']['body'], undefined>;
  headers: InputOr<D['request']['headers'], undefined>;
}

export type RouteResponse<D extends AnyRouteDescriptor> = z.infer<D['response']>;

/** `/tasks/{taskId}` → `/tasks/:taskId`. */
export function toHonoPath(path: string): string {
  return path.replace(/\{([A-Za-z0-9_]+)\}/g, ':$1');
}

/** Substitutes `{param}` placeholders with URI-encoded values. */
export function buildPath(path: string, params: Record<string, string | number> = {}): string {
  return path.replace(/\{([A-Za-z0-9_]+)\}/g, (_, name: string) => {
    const value = params[name];
    if (value === undefined) throw new Error(`Missing path parameter ${name}`);
    return encodeURIComponent(String(value));
  });
}

// ---------------------------------------------------------------------------------------------
// Shared schemas
// ---------------------------------------------------------------------------------------------

/**
 * Process roles of the platform binary. `exporter` (added by 09) forwards allow-listed events to an external
 * transport; it is a documented no-op until a non-Postgres `EventTransport` is bound.
 */
export const ProcessRole = z.enum(['api', 'worker', 'scheduler', 'exporter']).meta({ id: 'ProcessRole' });
export type ProcessRole = z.infer<typeof ProcessRole>;

/** Principal roles used by route guards. */
export const PLATFORM_ROLES = {
  requester: 'requester',
  approver: 'approver',
  operator: 'operator',
  admin: 'admin',
  csr: 'csr',
} as const;

const OPERATOR = [PLATFORM_ROLES.operator, PLATFORM_ROLES.admin] as const;

const TaskIdParams = z.object({ taskId: Id });
const OkResponse = z.object({ ok: z.literal(true) }).meta({ id: 'OkResponse' });

// auth ------------------------------------------------------------------------------------------
/**
 * `POST /auth/logout`. `logoutUrl`: when the session came from a company SSO login, the identity server's end-session
 * URL (OIDC RP-initiated logout); the browser must navigate there to end the SSO session as well.
 */
export const LogoutResponse = z
  .object({ ok: z.literal(true), logoutUrl: z.url().optional() })
  .meta({ id: 'LogoutResponse' });
export type LogoutResponse = z.infer<typeof LogoutResponse>;
export const LoginBody = z
  .object({ username: z.string().min(1), password: z.string().min(1) })
  .meta({ id: 'LoginBody' });
export const LoginResponse = z
  .object({ principal: Principal, expiresAt: IsoDateTime })
  .meta({ id: 'LoginResponse' });
export const MeResponse = z.object({ principal: Principal }).meta({ id: 'MeResponse' });

// tasks -----------------------------------------------------------------------------------------
/** Body of `POST /tasks`. Server sets taskId, tenantId, requestedBy; the idempotency key is a header. */
export const CreateTaskBody = z
  .object({
    capability: z.string().min(1).max(200),
    capabilityVersion: z.string().min(1).max(50).default('1'),
    domain: z.string().min(1).max(100),
    objective: z.string().min(1).max(4000),
    entityRefs: z.array(EntityRef).default([]),
    inputs: JsonObject.default({}),
    artifactRefs: z.array(z.string().min(1)).default([]),
    deadline: IsoDateTime.optional(),
    requestedActionScope: z.array(z.string().min(1)).default([]),
    channel: z.string().min(1).max(64).default('api'),
  })
  .meta({ id: 'CreateTaskBody' });
export type CreateTaskBody = z.infer<typeof CreateTaskBody>;

export const IdempotencyHeaders = z.object({ 'idempotency-key': IdempotencyKey });

/**
 * `replayed` is true when the idempotency key matched an existing task (HTTP 200 instead of 201). Added by 20a:
 * `POST /tasks` is a one-release adapter over `POST /cases` (`agent`); `taskId` and `caseId` name the episode and the
 * one-activity case it belongs to.
 */
export const CreateTaskResponse = z
  .object({ task: Task, replayed: z.boolean(), taskId: Id.optional(), caseId: Id.optional() })
  .meta({ id: 'CreateTaskResponse' });
export type CreateTaskResponse = z.infer<typeof CreateTaskResponse>;

export const TaskListQuery = z
  .object({
    state: TaskState.optional(),
    domain: z.string().optional(),
    capability: z.string().optional(),
    /** `true` = only tasks requested by the caller. */
    mine: z.enum(['true', 'false']).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().optional(),
  })
  .meta({ id: 'TaskListQuery' });
export type TaskListQuery = z.infer<typeof TaskListQuery>;

export const TaskListResponse = z
  .object({ items: z.array(Task), nextCursor: z.string().optional() })
  .meta({ id: 'TaskListResponse' });
export type TaskListResponse = z.infer<typeof TaskListResponse>;

/** Everything the case view needs for one task. Optional parts are absent until the owning module exists. */
export const TaskView = z
  .object({
    task: Task,
    /** Added by 20a: the case this task's agent activity belongs to (also `task.caseId`). */
    caseId: Id.optional(),
    /** Added by 20a: the activation of that agent activity (also `task.activationId`). */
    activationId: z.string().optional(),
    run: WorkflowRun.optional(),
    proposal: Proposal.optional(),
    approvals: z.array(Approval).default([]),
    receipts: z.array(ToolReceipt).default([]),
    evidence: z.array(Evidence).default([]),
    timeline: z.array(TimelineEntry).default([]),
  })
  .meta({ id: 'TaskView' });
export type TaskView = z.infer<typeof TaskView>;

export const CancelTaskBody = z
  .object({ reason: z.string().min(1).max(1000) })
  .meta({ id: 'CancelTaskBody' });
/** Answers keyed by `ClarificationQuestion.id`. */
export const ClarifyTaskBody = z
  .object({ answers: z.record(z.string(), z.unknown()) })
  .meta({ id: 'ClarifyTaskBody' });
export const ClarifyTaskResponse = z
  .object({ accepted: z.literal(true), task: Task })
  .meta({ id: 'ClarifyTaskResponse' });

/**
 * Response of `POST /tasks/{taskId}/attachments` (raw request body; `Content-Type` is the file type,
 * `?filename=` names it). `key` is the reference to cite in `artifactRefs` / timeline data (since 21c
 * `document:<documentId>`: the attachment is a document of the register, type `task.attachment`).
 */
export const TaskAttachmentQuery = z
  .object({ filename: z.string().min(1).max(200).default('attachment') })
  .meta({ id: 'TaskAttachmentQuery' });
export const TaskAttachmentResponse = z
  .object({
    key: z.string().min(1),
    filename: z.string(),
    contentType: z.string(),
    size: z.number().int().nonnegative(),
    sha256: z.string(),
    /** Added by 21c: the register row the attachment became (`DocumentStore`, type `task.attachment`). */
    documentId: z.string().min(1).optional(),
    /** Added by 21c: the Artifact holding the bytes. */
    artifactId: z.string().min(1).optional(),
  })
  .meta({ id: 'TaskAttachmentResponse' });
export type TaskAttachmentResponse = z.infer<typeof TaskAttachmentResponse>;

export const EpisodeTraceEntry = EpisodeTraceData.extend({ id: z.string().min(1) }).meta({
  id: 'EpisodeTraceEntry',
});
export type EpisodeTraceEntry = z.infer<typeof EpisodeTraceEntry>;

export const TaskTrace = z
  .object({
    taskId: Id,
    run: WorkflowRun.optional(),
    steps: z.array(WorkflowStepRecord).default([]),
    episode: z.array(EpisodeTraceEntry).default([]),
  })
  .meta({ id: 'TaskTrace' });
export type TaskTrace = z.infer<typeof TaskTrace>;

// approvals -------------------------------------------------------------------------------------
export const ApprovalListQuery = z
  .object({ status: ApprovalStatus.optional(), taskId: Id.optional() })
  .meta({ id: 'ApprovalListQuery' });
export const ApprovalListResponse = z
  .object({ items: z.array(Approval) })
  .meta({ id: 'ApprovalListResponse' });
export const ApprovalDetail = z
  .object({ approval: Approval, changePreviews: z.array(ChangePreview) })
  .meta({ id: 'ApprovalDetail' });
export type ApprovalDetail = z.infer<typeof ApprovalDetail>;
/**
 * `reviewedProposalHash` (14, optional): the `Approval.proposalHash` the client rendered when the decider reviewed it.
 * When present and different from the approval's current `proposalHash`, the decision is refused with
 * `approval_invalid` and `details.reason = 'changed_since_review'`. Omitted: behaviour as before.
 */
export const DecideApprovalBody = z
  .object({
    decision: z.enum(['approved', 'rejected']),
    rationale: z.string().min(1).max(4000),
    reviewedProposalHash: Sha256Hex.optional(),
  })
  .meta({ id: 'DecideApprovalBody' });
export type DecideApprovalBody = z.infer<typeof DecideApprovalBody>;

// registry --------------------------------------------------------------------------------------
export const RegistryStatus = z.enum(['active', 'disabled']).meta({ id: 'RegistryStatus' });
export const RegistryAgentView = z
  .object({
    definition: AgentDefinition,
    module: z.string(),
    status: RegistryStatus,
    killSwitch: z.boolean(),
    reason: z.string().optional(),
    admittedAt: IsoDateTime.optional(),
  })
  .meta({ id: 'RegistryAgentView' });
export type RegistryAgentView = z.infer<typeof RegistryAgentView>;
export const RegistryToolView = z
  .object({
    entry: ToolCatalogEntry,
    module: z.string(),
    status: RegistryStatus,
    killSwitch: z.boolean(),
    reason: z.string().optional(),
  })
  .meta({ id: 'RegistryToolView' });
export type RegistryToolView = z.infer<typeof RegistryToolView>;
export const RegistryAgentsResponse = z
  .object({ items: z.array(RegistryAgentView) })
  .meta({ id: 'RegistryAgentsResponse' });
export const RegistryToolsResponse = z
  .object({ items: z.array(RegistryToolView) })
  .meta({ id: 'RegistryToolsResponse' });
export const RegistryModelsResponse = z
  .object({
    items: z.array(ModelProfile),
    default: z.string(),
    /**
     * Added by 23: the effective configuration projection per profile name (decision B.5), the same data admin stats,
     * Studio and Settings show. Absent from servers older than 23.
     */
    effective: z.array(EffectiveModelProjection).optional(),
    /**
     * Added by 23 phase G: the deployment's default alias (`models.default` in the profile YAML). Agents whose
     * `spec.modelProfile` equals this name follow the selected `default` (ADR-006/ADR-022); every other name is a fixed
     * profile. `default` is the profile currently selected as default and may differ from the alias.
     */
    defaultAlias: z.string().min(1).optional(),
  })
  .meta({ id: 'RegistryModelsResponse' });
/**
 * A capability tasks can be created for (one entry per `AgentDefinition.spec.capabilities` item), with the
 * JSON Schema of `CreateTaskBody.inputs`. The workbench renders the create-task form from `inputSchema`.
 * Added by 02f (additive).
 */
export const CapabilityView = z
  .object({
    name: z.string().min(1),
    version: z.string().min(1),
    domain: z.string().min(1),
    /** Human title, e.g. "Supplier onboarding". */
    title: z.string().min(1),
    description: z.string().optional(),
    /** Agent that serves the capability. */
    agent: z.string().min(1),
    module: z.string(),
    /** JSON Schema (draft 2020-12, `type: object`) for `inputs`. `{}` when the pack publishes none. */
    inputSchema: JsonSchemaObject,
    status: RegistryStatus,
    killSwitch: z.boolean(),
  })
  .meta({ id: 'CapabilityView' });
export type CapabilityView = z.infer<typeof CapabilityView>;
export const RegistryCapabilitiesResponse = z
  .object({ items: z.array(CapabilityView) })
  .meta({ id: 'RegistryCapabilitiesResponse' });
export const SetDefaultModelBody = z
  .object({ profile: z.string().min(1) })
  .meta({ id: 'SetDefaultModelBody' });
/** `workloads` (21a): a workload without an AgentDefinition (a process service workload), by workload name. */
export const KillSwitchParams = z.object({
  kind: z.enum(['agents', 'tools', 'workloads']),
  name: z.string().min(1),
});
export const KillSwitchBody = z
  .object({ enabled: z.boolean(), reason: z.string().min(1).max(1000) })
  .meta({ id: 'KillSwitchBody' });
export const KillSwitchResponse = z
  .object({
    kind: z.enum(['agent', 'tool', 'workload']),
    name: z.string(),
    killSwitch: z.boolean(),
    changedAt: IsoDateTime,
    changedBy: z.string(),
  })
  .meta({ id: 'KillSwitchResponse' });

// modules ---------------------------------------------------------------------------------------
export const ModuleState = z
  .enum(['started', 'registered', 'failed', 'disabled'])
  .meta({ id: 'ModuleState' });
export type ModuleState = z.infer<typeof ModuleState>;
export const ModuleView = z
  .object({
    name: z.string(),
    version: z.string(),
    /** Package specifier the module was loaded from. */
    package: z.string(),
    type: z.enum(['core', 'pack', 'system']),
    enabled: z.boolean(),
    state: ModuleState,
    manifest: ModuleManifest,
  })
  .meta({ id: 'ModuleView' });
export type ModuleView = z.infer<typeof ModuleView>;
export const ModuleListResponse = z.object({ items: z.array(ModuleView) }).meta({ id: 'ModuleListResponse' });
export const ModuleToggleResponse = z
  .object({ name: z.string(), enabled: z.boolean(), restartRequired: z.literal(true) })
  .meta({ id: 'ModuleToggleResponse' });

// knowledge -------------------------------------------------------------------------------------
export const KnowledgeSourceListResponse = z
  .object({ items: z.array(KnowledgeSource) })
  .meta({ id: 'KnowledgeSourceListResponse' });
/** JSON upload. `multipart/form-data` with the same fields plus a `file` part is also accepted. */
export const KnowledgeUploadBody = z
  .object({
    name: z.string().min(1),
    scope: z.string().min(1),
    content: z.string(),
    contentType: z.string().default('text/markdown'),
    contentEncoding: z.enum(['utf8', 'base64']).default('utf8'),
    aclSubjects: z.array(z.string()).min(1),
    classification: Classification.default('internal'),
  })
  .meta({ id: 'KnowledgeUploadBody' });
export const KnowledgeSearchBody = z
  .object({
    query: z.string().min(1),
    scopes: z.array(z.string()).default([]),
    topK: z.number().int().min(1).max(50).default(8),
    /** Added by 21c (§1b.7): only sources whose `jurisdiction` metadata equals this pack-defined code. */
    jurisdiction: z.string().min(1).max(100).optional(),
    /** Added by 21c (§1b.7): "which version applied at time T" (`effectiveFrom <= at < effectiveTo`). */
    at: IsoDateTime.optional(),
  })
  .meta({ id: 'KnowledgeSearchBody' });
export const KnowledgeSearchResponse = z
  .object({ items: z.array(Evidence) })
  .meta({ id: 'KnowledgeSearchResponse' });

// ledger / audit --------------------------------------------------------------------------------
export const LedgerQuery = z.object({ taskId: Id }).meta({ id: 'LedgerQuery' });
export const LedgerResponse = z.object({ items: z.array(LedgerEntry) }).meta({ id: 'LedgerResponse' });
export const AuditQuery = z.object({ taskId: Id.optional() }).meta({ id: 'AuditQuery' });
export const AuditResponse = z
  .object({ items: z.array(AuditEntry), chainValid: z.boolean().optional() })
  .meta({ id: 'AuditResponse' });

// admin -----------------------------------------------------------------------------------------
export const HealthResponse = z
  .object({
    ok: z.boolean(),
    /**
     * Start-up work that requests depend on (e.g. `demo.seedOnStart`) is still running; the endpoint answers
     * 503 until it finishes. A failed task keeps 503 and reports its error in `startup[]`.
     */
    starting: z.boolean().optional(),
    startup: z
      .array(
        z.object({
          name: z.string(),
          state: z.enum(['pending', 'running', 'done', 'failed']),
          error: z.string().optional(),
        }),
      )
      .optional(),
    roles: z.array(ProcessRole),
    modules: z.array(z.object({ name: z.string(), version: z.string(), state: ModuleState })),
    profile: z.string().optional(),
    version: z.string().optional(),
    uptimeSeconds: z.number().nonnegative().optional(),
    /**
     * Added by 08 and 09 (guardrail 8): the adapter bound to each profile port, with its version when declared
     * and its self-description (e.g. the transport wake-up path) when it offers one.
     */
    adapters: z
      .array(
        z.object({
          port: z.string(),
          adapter: z.string(),
          version: z.string().optional(),
          detail: z.string().optional(),
        }),
      )
      .optional(),
  })
  .meta({ id: 'HealthResponse' });
export type HealthResponse = z.infer<typeof HealthResponse>;

export const AdminStats = z
  .object({
    tasksByState: z.record(z.string(), z.number().int().nonnegative()),
    successRate24h: z.number().min(0).max(1).nullable(),
    invalidActionRate: z.number().min(0).max(1).nullable(),
    unauthorizedAttempts: z.number().int().nonnegative(),
    unknownOutcomes: z.number().int().nonnegative(),
    approvalBacklog: z.number().int().nonnegative(),
    meanCostPerTaskUsd: z.number().nonnegative().nullable(),
    queueAgeSeconds: z.number().nonnegative().nullable(),
    /**
     * Adapter bound to each model profile. `offline` = model-mock: scripted answers for reasoning/extraction,
     * deterministic vectors for embeddings (e.g. the demo without `OPENAI_API_KEY`).
     */
    models: z
      .array(
        z.object({
          profile: z.string(),
          adapter: z.string(),
          model: z.string(),
          /** The id the adapter actually sends to the provider, when it maps `model` (e.g. an OpenRouter id). */
          servedModel: z.string().optional(),
          intendedTasks: z.array(z.string()),
          offline: z.boolean(),
          /** Added by 23: active managed version, connection and cost coverage of this profile's calls. */
          version: z.number().int().positive().optional(),
          connectionId: z.string().optional(),
          costStatus: ModelCostStatus.optional(),
          /** Known part of the cost (decimal string); null = unknown, never 0. */
          knownCostUsd: DecimalString.nullable().optional(),
        }),
      )
      .optional(),
    /**
     * Added by 23: call-record coverage behind `meanCostPerTaskUsd` (which is null when no call has a known cost).
     * `meanCostPerTaskStatus` says whether that mean covers all calls (`known`) or only priced ones (`partial`).
     */
    costCoverage: ModelCostCoverage.optional(),
    meanCostPerTaskStatus: ModelCostStatus.optional(),
    /** Added by 23: cost per requested/reported model from `ModelCallRecordQuery`. */
    costByModel: z.array(ModelCostAggregate).optional(),
    generatedAt: IsoDateTime,
  })
  .meta({ id: 'AdminStats' });
export type AdminStats = z.infer<typeof AdminStats>;

export const KillWorkerBody = z
  .object({ taskId: Id, phase: z.enum(['before_commit', 'after_commit']) })
  .meta({ id: 'KillWorkerBody' });
export const KillWorkerResponse = z
  .object({ armed: z.literal(true), taskId: Id, phase: z.enum(['before_commit', 'after_commit']) })
  .meta({ id: 'KillWorkerResponse' });
export const TamperApprovalBody = z
  .object({ approvalId: Id, actionId: Id, patch: JsonObject })
  .meta({ id: 'TamperApprovalBody' });
export const TamperApprovalResponse = z
  .object({ tampered: z.literal(true), approvalId: Id, actionId: Id, newArgsHash: z.string() })
  .meta({ id: 'TamperApprovalResponse' });

/** Body of `POST /admin/events/exporter/pause|resume` (11b): the reason is recorded in the audit entry. */
export const ExporterPauseBody = z
  .object({ reason: z.string().max(500).optional() })
  .meta({ id: 'ExporterPauseBody' });
/** State of the outbox exporter (11b): cursor in `core_events.export_cursor`, outbox end, admin pause. */
export const ExporterStatus = z
  .object({
    name: z.string(),
    /** Last outbox seq appended to the transport's log (absent before the exporter's first run). */
    cursorSeq: z.number().int().nonnegative().optional(),
    /** Highest outbox seq. */
    endSeq: z.number().int().nonnegative(),
    /** `endSeq − cursorSeq` (0 before the first run). */
    lag: z.number().int().nonnegative(),
    paused: z.boolean(),
    pausedAt: IsoDateTime.optional(),
    pausedBy: z.string().optional(),
    reason: z.string().optional(),
    updatedAt: IsoDateTime.optional(),
    /** EventTransport adapter bound in this process. */
    transport: z.string(),
    /** False when the bound transport keeps no log of its own (`append` absent): the exporter is a no-op. */
    exporting: z.boolean(),
  })
  .meta({ id: 'ExporterStatus' });
export type ExporterStatus = z.infer<typeof ExporterStatus>;

// demo ------------------------------------------------------------------------------------------
export const DemoScenario = z
  .object({
    id: z.string().min(1),
    pack: z.string().min(1),
    title: z.string().min(1),
    description: z.string(),
    /** Task capability (`kind: task`) or the process name (`kind: process`). */
    capability: z.string().min(1),
    /** Demo username that runs the scenario. */
    user: z.string().optional(),
    /**
     * Added by 17h: `task` (default when absent) creates one task; `process` starts a process (see `./demo.ts`).
     * Added by 20a: `agent` — a one-activity case (`POST /cases` with `agent`); `task` and absent mean the same for
     * one release.
     */
    kind: z.enum(['task', 'process', 'agent']).optional(),
    /** Process name and exact version (`kind: process`). */
    process: z.string().min(1).optional(),
    version: z.string().min(1).optional(),
    /** Case scope of the start request (`kind: process`). */
    scope: ResourceScope.optional(),
    /** Business input of the start (templates; `kind: process`). */
    input: z.record(z.string(), z.unknown()).optional(),
    /** Offline model-mock script for every agent activation (path relative to the module root). */
    modelScript: z.string().min(1).optional(),
    /** Ordered plan of mock-systems side events (`kind: process`). */
    sideEvents: z.array(DemoSideEvent).optional(),
    /** Unattended mode (`--auto`): deciders per role, scripted decisions and work-item outputs. */
    auto: DemoAutoConfig.optional(),
    /** Mock systems binding (base URL, webhook subscription, required seed records). */
    mock: DemoMockBinding.optional(),
  })
  .meta({ id: 'DemoScenario' });
export type DemoScenario = z.infer<typeof DemoScenario>;
export const DemoScenarioListResponse = z
  .object({ items: z.array(DemoScenario) })
  .meta({ id: 'DemoScenarioListResponse' });
export const DemoResetResponse = z
  .object({ ok: z.literal(true), durationMs: z.number().nonnegative(), modules: z.array(z.string()) })
  .meta({ id: 'DemoResetResponse' });
export const DemoRunBody = z
  .object({
    asUser: z.string().optional(),
    /** Added by 17h (process scenarios): decide gates and complete work items as the scenario's users. */
    auto: z.boolean().optional(),
    /** Added by 17h: stop following the case after this long (default 20 min with auto, 4 h without). */
    timeoutMs: z.number().int().positive().max(86_400_000).optional(),
  })
  .meta({ id: 'DemoRunBody' });
export const DemoRunResponse = z
  .object({
    scenarioId: z.string(),
    /** The created task; for a process scenario the process instance id (the run's task id). */
    taskId: Id,
    /** Added by 17h (process scenarios). Added by 20a: `agent` (one-activity case; `caseId` is set). */
    kind: z.enum(['task', 'process', 'agent']).optional(),
    runId: z.string().optional(),
    caseId: Id.optional(),
    processInstanceId: Id.optional(),
    /** Workbench path of the case (`/cases/<caseId>`). */
    caseUrl: z.string().optional(),
  })
  .meta({ id: 'DemoRunResponse' });
export const DemoRunListResponse = z
  .object({ items: z.array(DemoRunStatus) })
  .meta({ id: 'DemoRunListResponse' });

// model management (23, ADR-022) ----------------------------------------------------------------
/** Page size of every model-management list: 1..100, default 50. */
const ModelManagementLimit = z.coerce.number().int().min(1).max(100).default(50);
/** Opaque cursor returned as `nextCursor`; clients pass it back unchanged. */
const ModelManagementCursor = z.string().min(1).max(512);
/** Reason recorded in the management audit. */
const ManagementReason = z.string().trim().min(1).max(1000);

export const ModelConnectionsResponse = z
  .object({ items: z.array(ModelConnectionSummary) })
  .meta({ id: 'ModelConnectionsResponse' });
export type ModelConnectionsResponse = z.infer<typeof ModelConnectionsResponse>;

/** `GET /tasks/{taskId}/model-calls` page (added by 23 phase E). */
export const TaskModelCallsQuery = z
  .object({ limit: ModelManagementLimit, cursor: ModelManagementCursor.optional() })
  .meta({ id: 'TaskModelCallsQuery' });
export type TaskModelCallsQuery = z.infer<typeof TaskModelCallsQuery>;

/** Cost of a set of call records: known parts summed as decimals; null/`unknown` when none is priced. */
const TaskModelCallsCost = z.object({
  calls: z.number().int().nonnegative(),
  /** Sum of known/partial parts; null when no call of the set has a known cost. */
  knownCostUsd: DecimalString.nullable(),
  costStatus: ModelCostStatus,
  coverage: ModelCostCoverage,
});

/**
 * Durable model attribution of one task (added by 23 phase E, brief §9 execution detail): the call records of this
 * page ordered `(startedAt, callId)` and the immutable bindings they reference. Per call: requested profile and
 * pinned version (`bindings[].plan.requestedProfile`, `profileName@profileVersion`), configured model
 * (`requestedModel`), provider-reported model/provider, connection + revision, fallback relationship and reason,
 * usage, `costStatus`/`costUsd` (null = unknown, never 0). `cost` sums this page's calls only; `costTotal` (added by
 * the 23 review fixes, optional for older servers) is computed server-side over ALL calls of the task. No prompt,
 * completion or secret is part of a record.
 */
export const TaskModelCallsResponse = z
  .object({
    taskId: Id,
    items: z.array(ModelCallRecord),
    bindings: z.array(ModelBinding),
    cost: TaskModelCallsCost,
    costTotal: TaskModelCallsCost.optional(),
    nextCursor: z.string().optional(),
  })
  .meta({ id: 'TaskModelCallsResponse' });
export type TaskModelCallsResponse = z.infer<typeof TaskModelCallsResponse>;

/** `GET /registry/model-management/catalog` filters. `capability` = entries where it is `supported`. */
export const ModelCatalogQuery = z
  .object({
    connectionId: ModelConnectionId.optional(),
    operation: ModelOperation.optional(),
    approval: ApprovalState.optional(),
    availability: ModelAvailability.optional(),
    capability: ModelCapabilityName.optional(),
    /** Case-insensitive substring of model id or display name. */
    search: z.string().trim().max(200).optional(),
    limit: ModelManagementLimit,
    cursor: ModelManagementCursor.optional(),
  })
  .meta({ id: 'ModelCatalogQuery' });
export type ModelCatalogQuery = z.infer<typeof ModelCatalogQuery>;

/** Stable order: `(connectionId, modelId)` ascending; `nextCursor` absent on the last page. */
export const ModelCatalogPage = z
  .object({ items: z.array(ModelCatalogEntry), nextCursor: z.string().optional() })
  .meta({ id: 'ModelCatalogPage' });
export type ModelCatalogPage = z.infer<typeof ModelCatalogPage>;

export const ModelConnectionParams = z.object({ connectionId: ModelConnectionId });

/**
 * Body of `POST /connections/{connectionId}/refresh`. Strict: a refresh never accepts a URL, header or secret
 * reference; destinations come from deployment configuration only.
 */
export const RefreshModelCatalogBody = z
  .object({ reason: ManagementReason.optional() })
  .strict()
  .meta({ id: 'RefreshModelCatalogBody' });
export type RefreshModelCatalogBody = z.infer<typeof RefreshModelCatalogBody>;

/** Result of a complete refresh. A failed or partial refresh is an error response and keeps the last snapshot. */
export const RefreshModelCatalogResponse = z
  .object({
    connectionId: ModelConnectionId,
    fetchedAt: IsoDateTime,
    complete: z.literal(true),
    total: z.number().int().nonnegative(),
    added: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    /** Entries the snapshot omitted, now `unlisted` (never deleted or revoked). */
    unlisted: z.number().int().nonnegative(),
    warnings: z.array(z.string().max(500)).max(100).default([]),
  })
  .meta({ id: 'RefreshModelCatalogResponse' });
export type RefreshModelCatalogResponse = z.infer<typeof RefreshModelCatalogResponse>;

/** Body of `POST /catalog/manual`. The new entry is `unapproved`, `source: manual`, `capabilitySource: operator`. */
export const CreateManualModelEntryBody = z
  .object({ connectionId: ModelConnectionId, entry: ModelCatalogEntryInput, reason: ManagementReason })
  .strict()
  .meta({ id: 'CreateManualModelEntryBody' });
export type CreateManualModelEntryBody = z.infer<typeof CreateManualModelEntryBody>;

export const ModelCatalogEntryParams = z.object({ entryId: ModelCatalogEntryId });

/**
 * Body of `POST /catalog/{entryId}/approval`. `expectedRevision` = the entry's `approval.revision` (stale → `conflict`
 * with `details.currentRevision`). Approving an entry with operator-asserted capabilities needs
 * `acknowledgeAssertions: true`.
 */
export const ModelApprovalBody = z
  .object({
    decision: z.enum(['approve', 'revoke']),
    expectedRevision: z.number().int().nonnegative(),
    reason: ManagementReason,
    acknowledgeAssertions: z.boolean().default(false),
  })
  .strict()
  .meta({ id: 'ModelApprovalBody' });
export type ModelApprovalBody = z.infer<typeof ModelApprovalBody>;

/** Profile name path parameter (existing names like `reasoning-openai`; no `@` or `/`). */
export const ModelProfileParams = z.object({
  name: z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/, 'profile name: letters, digits, . _ -'),
});

export const ModelProfileListQuery = z
  .object({ limit: ModelManagementLimit, cursor: ModelManagementCursor.optional() })
  .meta({ id: 'ModelProfileListQuery' });

/** History line of one version (detail view lists up to the 100 newest). */
export const ModelProfileVersionSummary = z
  .object({
    version: z.number().int().positive(),
    hash: z.string(),
    createdAt: IsoDateTime,
    createdBy: z.string(),
    source: z.enum(['bootstrap', 'managed']),
    modelId: z.string(),
    connectionId: ModelConnectionId,
    active: z.boolean(),
  })
  .meta({ id: 'ModelProfileVersionSummary' });
export type ModelProfileVersionSummary = z.infer<typeof ModelProfileVersionSummary>;

/** One profile name: pointer, active version, effective projection and impacted agents. */
export const ModelProfileSummary = z
  .object({
    name: z.string(),
    pointer: ModelProfilePointer,
    active: ModelProfileVersion.nullable(),
    latestVersion: z.number().int().nonnegative(),
    isDefault: z.boolean(),
    effective: EffectiveModelProjection.nullable(),
    /** Agent names whose `spec.modelProfile` resolves to this name (including through the default alias). */
    impactedAgents: z.array(z.string()),
  })
  .meta({ id: 'ModelProfileSummary' });
export type ModelProfileSummary = z.infer<typeof ModelProfileSummary>;

export const ModelProfilesPage = z
  .object({
    items: z.array(ModelProfileSummary),
    default: ModelDefaultPointer,
    nextCursor: z.string().optional(),
  })
  .meta({ id: 'ModelProfilesPage' });
export type ModelProfilesPage = z.infer<typeof ModelProfilesPage>;

export const ModelProfileDetail = ModelProfileSummary.extend({
  history: z.array(ModelProfileVersionSummary).max(100),
  /** Full versions referenced by `history` (newest first, ≤100). */
  versions: z.array(ModelProfileVersion).max(100),
}).meta({ id: 'ModelProfileDetail' });
export type ModelProfileDetail = z.infer<typeof ModelProfileDetail>;

/**
 * Body of `POST /profiles/{name}/versions`: creates an immutable draft version (not active). `expectedRevision` is
 * the profile pointer's revision (0 for a new name). Operation, model id, connection and revision come from the
 * primary entry; fallback routes are snapshotted from the exact referenced versions.
 */
export const CreateModelProfileVersionBody = z
  .object({
    expectedRevision: z.number().int().nonnegative(),
    primaryEntryId: ModelCatalogEntryId,
    description: z.string().max(2000).optional(),
    owner: z.string().max(200).optional(),
    intendedTasks: z.array(z.string().min(1).max(64)).max(32).default([]),
    consequential: z.boolean().default(false),
    region: z.string().max(64).optional(),
    dataPolicy: z.string().max(64).optional(),
    latencyClass: z.enum(['interactive', 'standard', 'batch']).optional(),
    maxContextTokens: z.number().int().positive().optional(),
    embeddingDimensions: z.number().int().positive().optional(),
    settings: ModelSettings.default({}),
    requiredCapabilities: z.array(ModelCapabilityName).max(8).default([]),
    /** Operator-asserted capability values, recorded separately from provider facts. */
    operatorAssertions: ModelCapabilityAssertions.default({}),
    restrictions: z.array(ModelRestriction).max(16).default([]),
    fallback: z.array(ModelProfileVersionRef).max(3).default([]),
    evaluationRef: z.string().max(500).optional(),
    rationale: z.string().max(2000).optional(),
    acknowledgeUnlisted: z.boolean().default(false),
  })
  .strict()
  .meta({ id: 'CreateModelProfileVersionBody' });
export type CreateModelProfileVersionBody = z.infer<typeof CreateModelProfileVersionBody>;

/** Body of `POST /profiles/{name}/activate` (also rollback: activate an older exact version). */
export const ActivateModelProfileBody = z
  .object({
    version: z.number().int().positive(),
    expectedRevision: z.number().int().nonnegative(),
    reason: ManagementReason,
    acknowledgeUnlisted: z.boolean().default(false),
  })
  .strict()
  .meta({ id: 'ActivateModelProfileBody' });
export type ActivateModelProfileBody = z.infer<typeof ActivateModelProfileBody>;

/** Body of `POST /profiles/{name}/deactivate`. The current default cannot be deactivated (`conflict`, `default_in_use`). */
export const DeactivateModelProfileBody = z
  .object({ expectedRevision: z.number().int().nonnegative(), reason: ManagementReason })
  .strict()
  .meta({ id: 'DeactivateModelProfileBody' });
export type DeactivateModelProfileBody = z.infer<typeof DeactivateModelProfileBody>;

/** Body of `PATCH /registry/model-management/default`. `expectedRevision` = the default pointer's revision. */
export const SetManagedDefaultBody = z
  .object({
    profile: z.string().min(1).max(128),
    expectedRevision: z.number().int().nonnegative(),
    reason: ManagementReason.optional(),
  })
  .strict()
  .meta({ id: 'SetManagedDefaultBody' });
export type SetManagedDefaultBody = z.infer<typeof SetManagedDefaultBody>;

export const ManagedDefaultResponse = z
  .object({ default: ModelDefaultPointer, effective: z.array(EffectiveModelProjection) })
  .meta({ id: 'ManagedDefaultResponse' });
export type ManagedDefaultResponse = z.infer<typeof ManagedDefaultResponse>;

export const ModelManagementAuditQuery = z
  .object({
    operation: ModelManagementOperation.optional(),
    /** Profile name or entry id the record targets. */
    target: z.string().max(128).optional(),
    limit: ModelManagementLimit,
    cursor: ModelManagementCursor.optional(),
  })
  .meta({ id: 'ModelManagementAuditQuery' });
export type ModelManagementAuditQuery = z.infer<typeof ModelManagementAuditQuery>;

/** Newest first; stable by `(at desc, id desc)`. */
export const ModelManagementAuditPage = z
  .object({ items: z.array(ModelManagementAudit), nextCursor: z.string().optional() })
  .meta({ id: 'ModelManagementAuditPage' });
export type ModelManagementAuditPage = z.infer<typeof ModelManagementAuditPage>;

// ---------------------------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------------------------

export const authRoutes = {
  login: defineRoute({
    id: 'auth.login',
    method: 'post',
    path: '/auth/login',
    summary: 'Log in with username and password; sets the session cookie',
    tags: ['auth'],
    auth: 'public',
    request: { body: LoginBody },
    response: LoginResponse,
  }),
  logout: defineRoute({
    id: 'auth.logout',
    method: 'post',
    path: '/auth/logout',
    summary: 'End the current session (and name the SSO end-session URL to visit, if any)',
    tags: ['auth'],
    request: {},
    response: LogoutResponse,
  }),
  me: defineRoute({
    id: 'auth.me',
    method: 'get',
    path: '/auth/me',
    summary: 'Current principal',
    tags: ['auth'],
    request: {},
    response: MeResponse,
  }),
  /** Added by 08: sign-in options for the login screen (served by idp-chain; 404 under a single provider). */
  providers: defineRoute({
    id: 'auth.providers',
    method: 'get',
    path: '/auth/providers',
    summary: 'Sign-in providers for the login screen (company SSO, break-glass local administrator)',
    tags: ['auth'],
    auth: 'public',
    request: {},
    response: AuthProvidersResponse,
  }),
  /** Added by 08: the break-glass local administrator login of idp-chain (never tries SSO). */
  breakGlassLogin: defineRoute({
    id: 'auth.breakGlassLogin',
    method: 'post',
    path: '/auth/breakglass/login',
    summary: 'Break-glass local administrator login (idp-chain; audited; admin roles only)',
    tags: ['auth'],
    auth: 'public',
    request: { body: LoginBody },
    response: LoginResponse,
  }),
} as const;

export const taskRoutes = {
  create: defineRoute({
    id: 'tasks.create',
    method: 'post',
    path: '/tasks',
    summary:
      'Create a task (idempotent by Idempotency-Key: same key+body returns the same task with 200; different body → 409 idempotency_conflict)',
    tags: ['tasks'],
    request: { headers: IdempotencyHeaders, body: CreateTaskBody },
    response: CreateTaskResponse,
    successStatus: 201,
  }),
  list: defineRoute({
    /**
     * @deprecated 20a: no longer served (the list is `GET /cases`); kept so existing clients compile for one
     * release and left out of the OpenAPI export.
     */
    id: 'tasks.list',
    method: 'get',
    path: '/tasks',
    summary: 'List tasks, newest first',
    tags: ['tasks'],
    request: { query: TaskListQuery },
    response: TaskListResponse,
  }),
  get: defineRoute({
    id: 'tasks.get',
    method: 'get',
    path: '/tasks/{taskId}',
    summary: 'Task view: task, run, proposal, approvals, receipts, evidence, timeline',
    tags: ['tasks'],
    request: { params: TaskIdParams },
    response: TaskView,
  }),
  events: defineRoute({
    id: 'tasks.events',
    method: 'get',
    path: '/tasks/{taskId}/events',
    summary: 'Server-sent events for one task (event = type from SSE_EVENT_TYPES, data = PlatformEvent)',
    tags: ['tasks'],
    request: { params: TaskIdParams },
    response: PlatformEvent,
    responseType: 'sse',
  }),
  cancel: defineRoute({
    id: 'tasks.cancel',
    method: 'post',
    path: '/tasks/{taskId}/cancel',
    summary: 'Cancel a task (committed actions are reconciled or compensated, never forgotten)',
    tags: ['tasks'],
    request: { params: TaskIdParams, body: CancelTaskBody },
    response: Task,
  }),
  clarify: defineRoute({
    id: 'tasks.clarify',
    method: 'post',
    path: '/tasks/{taskId}/clarify',
    summary: 'Answer clarification questions of a task in awaiting_input',
    tags: ['tasks'],
    request: { params: TaskIdParams, body: ClarifyTaskBody },
    response: ClarifyTaskResponse,
  }),
  attachments: defineRoute({
    id: 'tasks.attachments',
    method: 'post',
    path: '/tasks/{taskId}/attachments',
    summary:
      'Upload an attachment for a task (raw body, Content-Type = file type, ?filename=); size cap and content-type allow-list apply',
    tags: ['tasks'],
    request: { params: TaskIdParams, query: TaskAttachmentQuery },
    response: TaskAttachmentResponse,
    successStatus: 201,
  }),
  trace: defineRoute({
    id: 'tasks.trace',
    method: 'get',
    path: '/tasks/{taskId}/trace',
    summary: 'Episode trace and workflow steps of a task',
    tags: ['tasks'],
    request: { params: TaskIdParams },
    response: TaskTrace,
  }),
  /** Added by 23 phase E: per-call model attribution under the task read authorization (served by apps/platform). */
  modelCalls: defineRoute({
    id: 'tasks.modelCalls',
    method: 'get',
    path: '/tasks/{taskId}/model-calls',
    summary:
      'Model call records and episode bindings of a task (profile, version, model, connection, cost status)',
    tags: ['tasks'],
    request: { params: TaskIdParams, query: TaskModelCallsQuery },
    response: TaskModelCallsResponse,
  }),
} as const;

export const approvalRoutes = {
  list: defineRoute({
    id: 'approvals.list',
    method: 'get',
    path: '/approvals',
    summary: 'List approvals',
    tags: ['approvals'],
    request: { query: ApprovalListQuery },
    response: ApprovalListResponse,
  }),
  get: defineRoute({
    id: 'approvals.get',
    method: 'get',
    path: '/approvals/{approvalId}',
    summary: 'Approval with change previews',
    tags: ['approvals'],
    request: { params: z.object({ approvalId: Id }) },
    response: ApprovalDetail,
  }),
  decide: defineRoute({
    id: 'approvals.decide',
    method: 'post',
    path: '/approvals/{approvalId}/decide',
    summary: 'Approve or reject (separation of duties enforced)',
    tags: ['approvals'],
    roles: [PLATFORM_ROLES.approver, PLATFORM_ROLES.admin],
    request: { params: z.object({ approvalId: Id }), body: DecideApprovalBody },
    response: Approval,
  }),
} as const;

export const registryRoutes = {
  agents: defineRoute({
    id: 'registry.agents',
    method: 'get',
    path: '/registry/agents',
    summary: 'Registered agents',
    tags: ['registry'],
    request: {},
    response: RegistryAgentsResponse,
  }),
  tools: defineRoute({
    id: 'registry.tools',
    method: 'get',
    path: '/registry/tools',
    summary: 'Tool catalog',
    tags: ['registry'],
    request: {},
    response: RegistryToolsResponse,
  }),
  models: defineRoute({
    id: 'registry.models',
    method: 'get',
    path: '/registry/models',
    summary: 'Model profiles and the default profile',
    tags: ['registry'],
    request: {},
    response: RegistryModelsResponse,
  }),
  capabilities: defineRoute({
    id: 'registry.capabilities',
    method: 'get',
    path: '/registry/capabilities',
    summary: 'Capabilities tasks can be created for, with the JSON Schema of their inputs',
    tags: ['registry'],
    request: {},
    response: RegistryCapabilitiesResponse,
  }),
  setDefaultModel: defineRoute({
    id: 'registry.setDefaultModel',
    method: 'patch',
    path: '/registry/models/default',
    summary: 'Set the default reasoning model profile',
    tags: ['registry'],
    roles: OPERATOR,
    request: { body: SetDefaultModelBody },
    response: RegistryModelsResponse,
  }),
  killSwitch: defineRoute({
    id: 'registry.killSwitch',
    method: 'post',
    path: '/registry/{kind}/{name}/kill-switch',
    summary: 'Engage or release the kill switch of an agent, tool or agent-less workload',
    tags: ['registry'],
    roles: OPERATOR,
    request: { params: KillSwitchParams, body: KillSwitchBody },
    response: KillSwitchResponse,
  }),
} as const;

/**
 * 23 (ADR-022): model management under `/registry/model-management`. Every route needs a session; mutations need
 * `operator|admin` **and** a principal in `models.management.tenantId` (checked by the server inside the mutation
 * transaction; body/header tenants are never read). With management disabled, mutations answer `forbidden` with
 * `details.reason: management_disabled` and reads serve the legacy projection.
 */
const MODEL_MGMT = ['model-management'] as const;
const MODEL_MGMT_PREFIX = '/registry/model-management';
export const modelManagementRoutes = {
  status: defineRoute({
    id: 'modelManagement.status',
    method: 'get',
    path: `${MODEL_MGMT_PREFIX}/status`,
    summary: 'Model management state, caller capabilities, scope and migration status',
    tags: MODEL_MGMT,
    request: {},
    response: ModelManagementStatus,
  }),
  connections: defineRoute({
    id: 'modelManagement.connections',
    method: 'get',
    path: `${MODEL_MGMT_PREFIX}/connections`,
    summary: 'Configured model connections (redacted) and readiness',
    tags: MODEL_MGMT,
    request: {},
    response: ModelConnectionsResponse,
  }),
  catalog: defineRoute({
    id: 'modelManagement.catalog',
    method: 'get',
    path: `${MODEL_MGMT_PREFIX}/catalog`,
    summary: 'Filtered, paginated model catalogue',
    tags: MODEL_MGMT,
    request: { query: ModelCatalogQuery },
    response: ModelCatalogPage,
  }),
  refresh: defineRoute({
    id: 'modelManagement.refresh',
    method: 'post',
    path: `${MODEL_MGMT_PREFIX}/connections/{connectionId}/refresh`,
    summary: 'Explicit bounded catalogue refresh of one configured connection',
    tags: MODEL_MGMT,
    roles: OPERATOR,
    request: { params: ModelConnectionParams, body: RefreshModelCatalogBody },
    response: RefreshModelCatalogResponse,
  }),
  createManualEntry: defineRoute({
    id: 'modelManagement.createManualEntry',
    method: 'post',
    path: `${MODEL_MGMT_PREFIX}/catalog/manual`,
    summary: 'Add a manual (unapproved) catalogue entry under a configured connection',
    tags: MODEL_MGMT,
    roles: OPERATOR,
    request: { body: CreateManualModelEntryBody },
    response: ModelCatalogEntry,
    successStatus: 201,
  }),
  approval: defineRoute({
    id: 'modelManagement.approval',
    method: 'post',
    path: `${MODEL_MGMT_PREFIX}/catalog/{entryId}/approval`,
    summary: 'Approve or revoke a catalogue entry',
    tags: MODEL_MGMT,
    roles: OPERATOR,
    request: { params: ModelCatalogEntryParams, body: ModelApprovalBody },
    response: ModelCatalogEntry,
  }),
  profiles: defineRoute({
    id: 'modelManagement.profiles',
    method: 'get',
    path: `${MODEL_MGMT_PREFIX}/profiles`,
    summary: 'Profiles with active version, effective model and impacted agents',
    tags: MODEL_MGMT,
    request: { query: ModelProfileListQuery },
    response: ModelProfilesPage,
  }),
  profile: defineRoute({
    id: 'modelManagement.profile',
    method: 'get',
    path: `${MODEL_MGMT_PREFIX}/profiles/{name}`,
    summary: 'One profile with version history and impacted agents',
    tags: MODEL_MGMT,
    request: { params: ModelProfileParams },
    response: ModelProfileDetail,
  }),
  createVersion: defineRoute({
    id: 'modelManagement.createVersion',
    method: 'post',
    path: `${MODEL_MGMT_PREFIX}/profiles/{name}/versions`,
    summary: 'Create an immutable draft profile version',
    tags: MODEL_MGMT,
    roles: OPERATOR,
    request: { params: ModelProfileParams, body: CreateModelProfileVersionBody },
    response: ModelProfileVersion,
    successStatus: 201,
  }),
  activate: defineRoute({
    id: 'modelManagement.activate',
    method: 'post',
    path: `${MODEL_MGMT_PREFIX}/profiles/{name}/activate`,
    summary: 'Activate an exact profile version (also rollback)',
    tags: MODEL_MGMT,
    roles: OPERATOR,
    request: { params: ModelProfileParams, body: ActivateModelProfileBody },
    response: ModelProfileSummary,
  }),
  deactivate: defineRoute({
    id: 'modelManagement.deactivate',
    method: 'post',
    path: `${MODEL_MGMT_PREFIX}/profiles/{name}/deactivate`,
    summary: 'Prevent new bindings of a profile (pinned attempts finish)',
    tags: MODEL_MGMT,
    roles: OPERATOR,
    request: { params: ModelProfileParams, body: DeactivateModelProfileBody },
    response: ModelProfileSummary,
  }),
  setDefault: defineRoute({
    id: 'modelManagement.setDefault',
    method: 'patch',
    path: `${MODEL_MGMT_PREFIX}/default`,
    summary: 'Select a compatible active profile as the platform default',
    tags: MODEL_MGMT,
    roles: OPERATOR,
    request: { body: SetManagedDefaultBody },
    response: ManagedDefaultResponse,
  }),
  audit: defineRoute({
    id: 'modelManagement.audit',
    method: 'get',
    path: `${MODEL_MGMT_PREFIX}/audit`,
    summary: 'Paginated model-management audit (management actors only)',
    tags: MODEL_MGMT,
    roles: OPERATOR,
    request: { query: ModelManagementAuditQuery },
    response: ModelManagementAuditPage,
  }),
} as const;

export const moduleRoutes = {
  list: defineRoute({
    id: 'modules.list',
    method: 'get',
    path: '/modules',
    summary: 'Installed modules and packs',
    tags: ['modules'],
    request: {},
    response: ModuleListResponse,
  }),
  enable: defineRoute({
    id: 'modules.enable',
    method: 'post',
    path: '/modules/{name}/enable',
    summary: 'Enable a module (writes profiles/overrides.local.yaml; restart required)',
    tags: ['modules'],
    roles: OPERATOR,
    request: { params: z.object({ name: z.string().min(1) }) },
    response: ModuleToggleResponse,
  }),
  disable: defineRoute({
    id: 'modules.disable',
    method: 'post',
    path: '/modules/{name}/disable',
    summary: 'Disable a module (writes profiles/overrides.local.yaml; restart required)',
    tags: ['modules'],
    roles: OPERATOR,
    request: { params: z.object({ name: z.string().min(1) }) },
    response: ModuleToggleResponse,
  }),
} as const;

export const knowledgeRoutes = {
  sources: defineRoute({
    id: 'knowledge.sources',
    method: 'get',
    path: '/knowledge/sources',
    summary: 'Knowledge sources visible to the caller',
    tags: ['knowledge'],
    request: {},
    response: KnowledgeSourceListResponse,
  }),
  upload: defineRoute({
    id: 'knowledge.upload',
    method: 'post',
    path: '/knowledge/sources',
    summary: 'Ingest a source (JSON body or multipart with a file part)',
    tags: ['knowledge'],
    roles: OPERATOR,
    request: { body: KnowledgeUploadBody },
    response: KnowledgeSource,
    successStatus: 201,
  }),
  delete: defineRoute({
    id: 'knowledge.delete',
    method: 'delete',
    path: '/knowledge/sources/{sourceId}',
    summary: 'Delete a source, its index entries, and expire derived memories',
    tags: ['knowledge'],
    roles: OPERATOR,
    request: { params: z.object({ sourceId: z.string().min(1) }) },
    response: OkResponse,
  }),
  search: defineRoute({
    id: 'knowledge.search',
    method: 'post',
    path: '/knowledge/search',
    summary: 'ACL-filtered retrieval (debug)',
    tags: ['knowledge'],
    roles: OPERATOR,
    request: { body: KnowledgeSearchBody },
    response: KnowledgeSearchResponse,
  }),
} as const;

export const ledgerRoutes = {
  actions: defineRoute({
    id: 'ledger.actions',
    method: 'get',
    path: '/ledger/actions',
    summary: 'Action ledger entries of a task',
    tags: ['ledger'],
    request: { query: LedgerQuery },
    response: LedgerResponse,
  }),
} as const;

export const auditRoutes = {
  list: defineRoute({
    id: 'audit.list',
    method: 'get',
    path: '/audit',
    summary: 'Audit entries (optionally for one task)',
    tags: ['audit'],
    request: { query: AuditQuery },
    response: AuditResponse,
  }),
} as const;

export const adminRoutes = {
  health: defineRoute({
    id: 'admin.health',
    method: 'get',
    path: '/admin/health',
    summary: 'Liveness, process roles and module states',
    tags: ['admin'],
    auth: 'public',
    request: {},
    response: HealthResponse,
  }),
  stats: defineRoute({
    id: 'admin.stats',
    method: 'get',
    path: '/admin/stats',
    summary: 'Governance dashboard numbers',
    tags: ['admin'],
    roles: OPERATOR,
    request: {},
    response: AdminStats,
  }),
  /** Outbox exporter (11b): status, and an audited pause / resume shared by every replica (Postgres). */
  exporter: {
    status: defineRoute({
      id: 'admin.exporter.status',
      method: 'get',
      path: '/admin/events/exporter',
      summary: 'Outbox exporter cursor, lag and pause state',
      tags: ['admin', 'events'],
      roles: OPERATOR,
      request: {},
      response: ExporterStatus,
    }),
    pause: defineRoute({
      id: 'admin.exporter.pause',
      method: 'post',
      path: '/admin/events/exporter/pause',
      summary: 'Pause the outbox exporter (audited); the outbox keeps every event',
      tags: ['admin', 'events'],
      roles: OPERATOR,
      request: { body: ExporterPauseBody },
      response: ExporterStatus,
    }),
    resume: defineRoute({
      id: 'admin.exporter.resume',
      method: 'post',
      path: '/admin/events/exporter/resume',
      summary: 'Resume the outbox exporter from its cursor (audited)',
      tags: ['admin', 'events'],
      roles: OPERATOR,
      request: { body: ExporterPauseBody },
      response: ExporterStatus,
    }),
  },
  chaos: {
    killWorker: defineRoute({
      id: 'admin.chaos.killWorker',
      method: 'post',
      path: '/admin/chaos/kill-worker',
      summary: 'Arm a simulated worker crash before or after the next action commit of a task',
      tags: ['admin', 'chaos'],
      roles: OPERATOR,
      request: { body: KillWorkerBody },
      response: KillWorkerResponse,
    }),
    tamperApproval: defineRoute({
      id: 'admin.chaos.tamperApproval',
      method: 'post',
      path: '/admin/chaos/tamper-approval',
      summary: 'Mutate the stored args of an approved action (demo only) so the recheck fails',
      tags: ['admin', 'chaos'],
      roles: OPERATOR,
      request: { body: TamperApprovalBody },
      response: TamperApprovalResponse,
    }),
  },
} as const;

export const demoRoutes = {
  reset: defineRoute({
    id: 'demo.reset',
    method: 'post',
    path: '/demo/reset',
    summary: 'Restore demo seed state',
    tags: ['demo'],
    roles: OPERATOR,
    request: {},
    response: DemoResetResponse,
  }),
  scenarios: {
    list: defineRoute({
      id: 'demo.scenarios.list',
      method: 'get',
      path: '/demo/scenarios',
      summary: 'Demo scenarios contributed by packs',
      tags: ['demo'],
      request: {},
      response: DemoScenarioListResponse,
    }),
    run: defineRoute({
      id: 'demo.scenarios.run',
      method: 'post',
      path: '/demo/scenarios/{scenarioId}/run',
      summary: 'Run a demo scenario; returns the created task',
      tags: ['demo'],
      roles: OPERATOR,
      request: { params: z.object({ scenarioId: z.string().min(1) }), body: DemoRunBody },
      response: DemoRunResponse,
      successStatus: 201,
    }),
  },
  /** Added by 17h: process scenario runs (in-memory, per API process). */
  runs: {
    list: defineRoute({
      id: 'demo.runs.list',
      method: 'get',
      path: '/demo/runs',
      summary: 'Process scenario runs of this API process (newest first)',
      tags: ['demo'],
      roles: OPERATOR,
      request: {},
      response: DemoRunListResponse,
    }),
    get: defineRoute({
      id: 'demo.runs.get',
      method: 'get',
      path: '/demo/runs/{runId}',
      summary: 'One process scenario run: progress lines, side events, what is waiting for whom',
      tags: ['demo'],
      roles: OPERATOR,
      request: { params: z.object({ runId: z.string().min(1) }) },
      response: DemoRunStatus,
    }),
    stop: defineRoute({
      id: 'demo.runs.stop',
      method: 'post',
      path: '/demo/runs/{runId}/stop',
      summary: 'Stop following a run (the case itself continues)',
      tags: ['demo'],
      roles: OPERATOR,
      request: { params: z.object({ runId: z.string().min(1) }) },
      response: DemoRunStatus,
    }),
  },
} as const;

export const metricsRoutes = {
  scrape: defineRoute({
    id: 'metrics.scrape',
    method: 'get',
    path: '/metrics',
    summary: 'Prometheus text exposition',
    tags: ['metrics'],
    auth: 'public',
    request: {},
    response: z.string(),
    responseType: 'text',
  }),
} as const;

// studio (added by 12) --------------------------------------------------------------------------
/**
 * Agent studio (`modules/agent-studio`, opt-in): authoring of registry bundles (11a). Routes carry no `roles`;
 * the module checks `studio.*` permissions (role catalog ∪ `modules.config.agent-studio.grants`) and each summary
 * names the permission. With the module disabled none of these routes is mounted (404).
 */

/** Authoring state of one bundle version (the module's lifecycle; the registry stores only admitted/retired). */
export const StudioVersionState = z
  .enum(['draft', 'submitted', 'evaluating', 'eval_passed', 'eval_failed', 'published', 'retired'])
  .meta({ id: 'StudioVersionState' });
export type StudioVersionState = z.infer<typeof StudioVersionState>;

/** The registry's decision on a published version (`registry.catalog.changed`). */
export const StudioAdmission = z.enum(['pending', 'admitted', 'refused']).meta({ id: 'StudioAdmission' });
export type StudioAdmission = z.infer<typeof StudioAdmission>;

/** A problem of a draft, with the file or field it is about (`errors` block submit; `warnings` do not). */
export const StudioIssue = z
  .object({
    path: z.string(),
    code: z.string(),
    message: z.string(),
    level: z.enum(['error', 'warning']).default('error'),
  })
  .meta({ id: 'StudioIssue' });
export type StudioIssue = z.infer<typeof StudioIssue>;

const StudioAgentName = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9.-]*$/, 'agent name must be lowercase kebab/dot case');
const StudioDomain = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9.-]*$/, 'domain must be lowercase kebab/dot case');

export const StudioMetaResponse = z
  .object({
    enabled: z.literal(true),
    /** Module version. */
    version: z.string(),
    principal: z.object({ id: z.string(), displayName: z.string(), roles: z.array(z.string()) }),
    /** The caller's `studio.*` permissions (roles ∪ grants). */
    permissions: z.array(z.string()),
    /** `modules.config.agent-studio.limitsCeiling` and the autonomy classes a bundle may declare. */
    ceilings: z.object({
      maxModelTurns: z.number().int().positive(),
      maxToolCalls: z.number().int().nonnegative(),
      deadlineSeconds: z.number().int().positive(),
      maxDelegationDepth: z.number().int().nonnegative(),
      autonomy: z.array(AutonomyClass),
    }),
    /** UTF-8 byte limits (11a `BUNDLE_LIMITS` plus the prompt limit). */
    limits: z.object({
      fileBytes: z.number().int().positive(),
      totalBytes: z.number().int().positive(),
      totalWithEvalsBytes: z.number().int().positive(),
      schemaBytes: z.number().int().positive(),
      promptBytes: z.number().int().positive(),
    }),
    publish: z.object({ requireDistinctPublisher: z.boolean() }),
    /** "Draft with AI": the capability the SPA creates a task for (`POST /tasks`). */
    ai: z.object({
      capability: z.string(),
      capabilityVersion: z.string(),
      domain: z.string(),
      available: z.boolean(),
    }),
  })
  .meta({ id: 'StudioMetaResponse' });
export type StudioMetaResponse = z.infer<typeof StudioMetaResponse>;

export const StudioCatalogTool = z
  .object({
    name: z.string(),
    version: z.string(),
    purpose: z.string(),
    module: z.string(),
    sideEffectClass: SideEffectClass,
    autonomyClass: AutonomyClass,
    approvalPolicy: z.enum(['none', 'required', 'policy']),
    requiredScopes: z.array(z.string()),
    status: RegistryStatus,
    killSwitch: z.boolean(),
  })
  .meta({ id: 'StudioCatalogTool' });
export type StudioCatalogTool = z.infer<typeof StudioCatalogTool>;
export const StudioCatalogToolsResponse = z
  .object({ items: z.array(StudioCatalogTool) })
  .meta({ id: 'StudioCatalogToolsResponse' });

export const StudioCatalogPolicy = z
  .object({
    name: z.string(),
    version: z.string(),
    module: z.string(),
    description: z.string().optional(),
    rules: z.number().int().nonnegative(),
  })
  .meta({ id: 'StudioCatalogPolicy' });
export type StudioCatalogPolicy = z.infer<typeof StudioCatalogPolicy>;
export const StudioCatalogPoliciesResponse = z
  .object({ items: z.array(StudioCatalogPolicy) })
  .meta({ id: 'StudioCatalogPoliciesResponse' });

export const StudioCatalogEvaluation = z
  .object({
    /** Evaluation name or pattern (`*-correctness-v1`); pack evaluations are `<module>.<name>`. */
    name: z.string(),
    suite: z.string(),
    description: z.string(),
    /** Added by 17c0: platform catalog or the pack that declares it in `provides.evaluations`. */
    provenance: EvalProvenance.optional(),
    /** Added by 17c0: pack `dataset` evaluations — JSONL path relative to the pack root. */
    datasetRef: z.string().optional(),
  })
  .meta({ id: 'StudioCatalogEvaluation' });
/** Added by 17c0. */
export type StudioCatalogEvaluation = z.infer<typeof StudioCatalogEvaluation>;
export const StudioCatalogEvaluationsResponse = z
  .object({ items: z.array(StudioCatalogEvaluation) })
  .meta({ id: 'StudioCatalogEvaluationsResponse' });

export const StudioCatalogAgent = z
  .object({
    name: z.string(),
    version: z.string(),
    domain: z.string(),
    description: z.string().optional(),
    module: z.string(),
    capabilities: z.array(z.object({ name: z.string(), version: z.string() })),
    /** True for registry bundles (module `bundles`). */
    bundle: z.boolean(),
    status: RegistryStatus,
    killSwitch: z.boolean(),
    /** "Copy an agent": the input schema comes from the capability view; the output only from `.json`/`.yaml`. */
    copyable: z.boolean(),
    copyNote: z.string().optional(),
  })
  .meta({ id: 'StudioCatalogAgent' });
export type StudioCatalogAgent = z.infer<typeof StudioCatalogAgent>;
export const StudioCatalogAgentsResponse = z
  .object({ items: z.array(StudioCatalogAgent) })
  .meta({ id: 'StudioCatalogAgentsResponse' });

export const StudioTemplate = z
  .object({
    /** `blank` or `<module>/<folder>` for pack-folder templates. */
    id: z.string(),
    title: z.string(),
    description: z.string(),
    source: z.enum(['builtin', 'pack']),
    module: z.string().optional(),
    agent: z.object({
      name: z.string(),
      version: z.string(),
      domain: z.string(),
      capability: z.string().optional(),
      autonomy: AutonomyClass.optional(),
      tools: z.array(z.string()).default([]),
    }),
  })
  .meta({ id: 'StudioTemplate' });
export type StudioTemplate = z.infer<typeof StudioTemplate>;
export const StudioTemplatesResponse = z
  .object({ items: z.array(StudioTemplate) })
  .meta({ id: 'StudioTemplatesResponse' });

export const StudioVersionSummary = z
  .object({
    version: SemVer,
    state: StudioVersionState,
    revision: z.number().int().positive(),
    hash: z.string(),
    admission: StudioAdmission.optional(),
    admissionReason: z.string().optional(),
    publishedAt: IsoDateTime.optional(),
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'StudioVersionSummary' });
export type StudioVersionSummary = z.infer<typeof StudioVersionSummary>;

export const StudioBundleSummary = z
  .object({
    /** Route key of the bundle: the agent name (unique, never reused after retirement). */
    bundleId: z.string(),
    name: z.string(),
    domain: z.string(),
    tenantId: z.string(),
    description: z.string().optional(),
    retired: z.boolean(),
    retiredAt: IsoDateTime.optional(),
    origin: BundleOrigin,
    latest: StudioVersionSummary,
    /** Highest published version, and the highest the registry admitted. */
    publishedVersion: z.string().optional(),
    admittedVersion: z.string().optional(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'StudioBundleSummary' });
export type StudioBundleSummary = z.infer<typeof StudioBundleSummary>;

export const StudioBundleListQuery = z
  .object({
    /** Drafts created by this task ("Draft with AI"). */
    originTaskId: Id.optional(),
    state: StudioVersionState.optional(),
  })
  .meta({ id: 'StudioBundleListQuery' });
export const StudioBundleListResponse = z
  .object({ items: z.array(StudioBundleSummary) })
  .meta({ id: 'StudioBundleListResponse' });
export const StudioBundleDetail = z
  .object({ bundle: StudioBundleSummary, versions: z.array(StudioVersionSummary) })
  .meta({ id: 'StudioBundleDetail' });
export type StudioBundleDetail = z.infer<typeof StudioBundleDetail>;

export const StudioEvalRunView = z
  .object({
    evalRunId: z.string(),
    version: z.string(),
    hash: z.string(),
    /** `superseded`: the draft changed (or was reopened) before the result arrived. */
    status: z.enum(['requested', 'passed', 'failed', 'error', 'superseded']),
    requestedBy: z.string(),
    requestedAt: IsoDateTime,
    completedAt: IsoDateTime.optional(),
    result: BundleEvalCompletedData.optional(),
  })
  .meta({ id: 'StudioEvalRunView' });
export type StudioEvalRunView = z.infer<typeof StudioEvalRunView>;
export const StudioEvalRunsResponse = z
  .object({ items: z.array(StudioEvalRunView) })
  .meta({ id: 'StudioEvalRunsResponse' });

export const StudioVersionView = z
  .object({
    bundleId: z.string(),
    name: z.string(),
    domain: z.string(),
    version: SemVer,
    state: StudioVersionState,
    /** Increments on every edit; updates must send the revision they edited. */
    revision: z.number().int().positive(),
    /** `bundleHash(files)` (files without `evals/*`). */
    hash: z.string(),
    origin: BundleOrigin,
    authors: z.array(z.string()),
    publisher: z.string().optional(),
    publishedAt: IsoDateTime.optional(),
    admission: StudioAdmission.optional(),
    admissionReason: z.string().optional(),
    /** AI drafts must be marked reviewed before publish. */
    requiresReview: z.boolean(),
    reviewed: z.boolean(),
    /** Pack-folder path → text (agent YAML, prompt, schemas, policy, policy tests, eval JSONL and scripts). */
    files: z.record(z.string(), z.string()),
    /** The exact `AgentDefinition` the registry would admit (absent while the agent file does not parse). */
    definition: AgentDefinition.optional(),
    issues: z.array(StudioIssue),
    gate: z.object({
      canSubmit: z.boolean(),
      canPublish: z.boolean(),
      /** Why submit or publish is not possible now. */
      reasons: z.array(z.string()),
    }),
    lastEval: StudioEvalRunView.optional(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'StudioVersionView' });
export type StudioVersionView = z.infer<typeof StudioVersionView>;

/** `POST /studio/bundles`: start a draft from blank, a template or a registered agent. */
export const CreateStudioBundleBody = z
  .discriminatedUnion('from', [
    z.object({
      from: z.literal('blank'),
      name: StudioAgentName,
      domain: StudioDomain,
      description: z.string().max(1000).optional(),
    }),
    z.object({
      from: z.literal('template'),
      template: z.string().min(1).max(200),
      /** Defaults to the template agent's name. */
      name: StudioAgentName.optional(),
      domain: StudioDomain.optional(),
    }),
    z.object({
      from: z.literal('agent'),
      agent: z.string().min(1).max(200),
      /** A new name (the source agent keeps its own). */
      name: StudioAgentName,
      domain: StudioDomain.optional(),
    }),
  ])
  .meta({ id: 'CreateStudioBundleBody' });
export type CreateStudioBundleBody = z.infer<typeof CreateStudioBundleBody>;

/** `POST /studio/import`: a pack folder's data files (code is refused). */
export const StudioImportBody = z
  .object({ files: z.record(z.string(), z.string()), name: StudioAgentName.optional() })
  .meta({ id: 'StudioImportBody' });

/**
 * Structured edits (form): each part present replaces that file. The module writes the files (YAML in contract
 * key order); `definition` keeps the stored name, version and the fixed refs. `policyTests: null` removes the table.
 */
export const StudioParts = z
  .object({
    definition: AgentDefinition.optional(),
    prompt: z.string().optional(),
    inputSchema: JsonSchemaObject.optional(),
    outputSchema: JsonSchemaObject.optional(),
    policy: PolicyBundle.optional(),
    policyTests: z.union([PolicyTestTable, z.null()]).optional(),
    /** `evals/<name>.jsonl` text. */
    evals: z.string().optional(),
    /** `evals/scripts/<file>.json` texts by file name (`null` removes one). */
    evalScripts: z.record(z.string(), z.union([z.string(), z.null()])).optional(),
  })
  .meta({ id: 'StudioParts' });
export type StudioParts = z.infer<typeof StudioParts>;

export const UpdateStudioVersionBody = StudioParts.extend({
  revision: z.number().int().positive(),
  /** Marks an AI draft as reviewed by the caller. */
  reviewed: z.boolean().optional(),
}).meta({ id: 'UpdateStudioVersionBody' });
export type UpdateStudioVersionBody = z.infer<typeof UpdateStudioVersionBody>;

/** `PUT …/yaml`: one YAML file as text (default the agent file). Comments are not kept. */
export const StudioYamlBody = z
  .object({
    revision: z.number().int().positive(),
    path: z.string().max(200).optional(),
    text: z.string().max(64 * 1024),
  })
  .meta({ id: 'StudioYamlBody' });
export type StudioYamlBody = z.infer<typeof StudioYamlBody>;

export const StudioValidateResponse = z
  .object({
    ok: z.boolean(),
    definition: AgentDefinition.optional(),
    files: z.record(z.string(), z.string()),
    hash: z.string(),
    issues: z.array(StudioIssue),
  })
  .meta({ id: 'StudioValidateResponse' });
export type StudioValidateResponse = z.infer<typeof StudioValidateResponse>;

export const StudioRevisionBody = z
  .object({ revision: z.number().int().positive() })
  .meta({ id: 'StudioRevisionBody' });
export const StudioNewVersionBody = z
  .object({
    /** Version to copy (default: the highest published one). */
    from: SemVer.optional(),
    bump: z.enum(['patch', 'minor', 'major']),
  })
  .meta({ id: 'StudioNewVersionBody' });
export const StudioPublishBody = z
  .object({ note: z.string().max(500).optional() })
  .meta({ id: 'StudioPublishBody' });
export const StudioRetireBody = z
  .object({ reason: z.string().min(1).max(500) })
  .meta({ id: 'StudioRetireBody' });
export const StudioExportQuery = z
  .object({ format: z.enum(['files', 'tar']).default('files') })
  .meta({ id: 'StudioExportQuery' });
export const StudioExportResponse = z
  .object({
    name: z.string(),
    version: z.string(),
    hash: z.string(),
    /** The pack projection (`agentic bundle export` layout) including the version's `evals/*`. */
    files: z.record(z.string(), z.string()),
  })
  .meta({ id: 'StudioExportResponse' });

const StudioBundleParams = z.object({ bundleId: z.string().min(1).max(100) });
const StudioVersionParams = StudioBundleParams.extend({ version: SemVer });
const STUDIO = ['studio'] as const;

export const studioRoutes = {
  meta: defineRoute({
    id: 'studio.meta',
    method: 'get',
    path: '/studio/meta',
    summary: 'Studio status, caller permissions, ceilings and size limits (any session)',
    tags: STUDIO,
    request: {},
    response: StudioMetaResponse,
  }),
  catalog: {
    tools: defineRoute({
      id: 'studio.catalog.tools',
      method: 'get',
      path: '/studio/catalog/tools',
      summary: 'Tools a bundle may use (studio.draft)',
      tags: STUDIO,
      request: {},
      response: StudioCatalogToolsResponse,
    }),
    policies: defineRoute({
      id: 'studio.catalog.policies',
      method: 'get',
      path: '/studio/catalog/policies',
      summary: 'Registered policy bundles (studio.draft)',
      tags: STUDIO,
      request: {},
      response: StudioCatalogPoliciesResponse,
    }),
    models: defineRoute({
      id: 'studio.catalog.models',
      method: 'get',
      path: '/studio/catalog/models',
      summary: 'Model profiles (studio.draft)',
      tags: STUDIO,
      request: {},
      response: RegistryModelsResponse,
    }),
    evaluations: defineRoute({
      id: 'studio.catalog.evaluations',
      method: 'get',
      path: '/studio/catalog/evaluations',
      summary: 'Evaluations the eval gate can run (studio.draft)',
      tags: STUDIO,
      request: {},
      response: StudioCatalogEvaluationsResponse,
    }),
    agents: defineRoute({
      id: 'studio.catalog.agents',
      method: 'get',
      path: '/studio/catalog/agents',
      summary: 'Registered agents ("Copy an agent") (studio.draft)',
      tags: STUDIO,
      request: {},
      response: StudioCatalogAgentsResponse,
    }),
  },
  templates: defineRoute({
    id: 'studio.templates',
    method: 'get',
    path: '/studio/templates',
    summary: 'Blank and pack-folder templates (studio.draft)',
    tags: STUDIO,
    request: {},
    response: StudioTemplatesResponse,
  }),
  bundles: {
    list: defineRoute({
      id: 'studio.bundles.list',
      method: 'get',
      path: '/studio/bundles',
      summary: 'Bundles of the tenant, newest first (studio.draft)',
      tags: STUDIO,
      request: { query: StudioBundleListQuery },
      response: StudioBundleListResponse,
    }),
    create: defineRoute({
      id: 'studio.bundles.create',
      method: 'post',
      path: '/studio/bundles',
      summary: 'Start a draft from blank, a template or an agent (studio.draft)',
      tags: STUDIO,
      request: { headers: IdempotencyHeaders, body: CreateStudioBundleBody },
      response: StudioVersionView,
      successStatus: 201,
    }),
    import: defineRoute({
      id: 'studio.bundles.import',
      method: 'post',
      path: '/studio/import',
      summary: 'Import a pack folder (data files only) as a draft (studio.draft)',
      tags: STUDIO,
      request: { headers: IdempotencyHeaders, body: StudioImportBody },
      response: StudioVersionView,
      successStatus: 201,
    }),
    get: defineRoute({
      id: 'studio.bundles.get',
      method: 'get',
      path: '/studio/bundles/{bundleId}',
      summary: 'One bundle and its versions (studio.draft)',
      tags: STUDIO,
      request: { params: StudioBundleParams },
      response: StudioBundleDetail,
    }),
    newVersion: defineRoute({
      id: 'studio.bundles.newVersion',
      method: 'post',
      path: '/studio/bundles/{bundleId}/versions',
      summary: 'New draft version (patch, minor, major) copied from a published one (studio.draft)',
      tags: STUDIO,
      request: { params: StudioBundleParams, headers: IdempotencyHeaders, body: StudioNewVersionBody },
      response: StudioVersionView,
      successStatus: 201,
    }),
    retire: defineRoute({
      id: 'studio.bundles.retire',
      method: 'post',
      path: '/studio/bundles/{bundleId}/retire',
      summary: 'Retire the bundle agent, every version, for good (studio.retire; audited)',
      tags: STUDIO,
      request: { params: StudioBundleParams, headers: IdempotencyHeaders, body: StudioRetireBody },
      response: StudioBundleDetail,
    }),
  },
  versions: {
    get: defineRoute({
      id: 'studio.versions.get',
      method: 'get',
      path: '/studio/bundles/{bundleId}/versions/{version}',
      summary: 'One version: files, exact AgentDefinition, issues, gate (studio.draft)',
      tags: STUDIO,
      request: { params: StudioVersionParams },
      response: StudioVersionView,
    }),
    update: defineRoute({
      id: 'studio.versions.update',
      method: 'put',
      path: '/studio/bundles/{bundleId}/versions/{version}',
      summary: 'Edit a draft from the form (revision + 1) (studio.draft)',
      tags: STUDIO,
      request: { params: StudioVersionParams, headers: IdempotencyHeaders, body: UpdateStudioVersionBody },
      response: StudioVersionView,
    }),
    yaml: defineRoute({
      id: 'studio.versions.yaml',
      method: 'put',
      path: '/studio/bundles/{bundleId}/versions/{version}/yaml',
      summary: 'Edit one YAML file of a draft as text (comments are dropped) (studio.draft)',
      tags: STUDIO,
      request: { params: StudioVersionParams, headers: IdempotencyHeaders, body: StudioYamlBody },
      response: StudioVersionView,
    }),
    validate: defineRoute({
      id: 'studio.versions.validate',
      method: 'post',
      path: '/studio/bundles/{bundleId}/versions/{version}/validate',
      summary: 'Validate stored or unsaved parts; the exact AgentDefinition preview (studio.draft)',
      tags: STUDIO,
      request: { params: StudioVersionParams, body: StudioParts },
      response: StudioValidateResponse,
    }),
    submit: defineRoute({
      id: 'studio.versions.submit',
      method: 'post',
      path: '/studio/bundles/{bundleId}/versions/{version}/submit',
      summary: 'Submit a draft for catalog checks and the eval gate (studio.draft)',
      tags: STUDIO,
      request: { params: StudioVersionParams, headers: IdempotencyHeaders, body: StudioRevisionBody },
      response: StudioVersionView,
    }),
    reopen: defineRoute({
      id: 'studio.versions.reopen',
      method: 'post',
      path: '/studio/bundles/{bundleId}/versions/{version}/reopen',
      summary: 'Back to draft after an eval result or a failed check (studio.draft)',
      tags: STUDIO,
      request: { params: StudioVersionParams, headers: IdempotencyHeaders, body: StudioRevisionBody },
      response: StudioVersionView,
    }),
    evals: defineRoute({
      id: 'studio.versions.evals',
      method: 'get',
      path: '/studio/bundles/{bundleId}/versions/{version}/eval',
      summary: 'Eval runs of a version, newest first (studio.draft)',
      tags: STUDIO,
      request: { params: StudioVersionParams },
      response: StudioEvalRunsResponse,
    }),
    export: defineRoute({
      id: 'studio.versions.export',
      method: 'get',
      path: '/studio/bundles/{bundleId}/versions/{version}/export',
      summary: 'Pack projection as { files } or a tar archive (?format=tar) (studio.draft)',
      tags: STUDIO,
      request: { params: StudioVersionParams, query: StudioExportQuery },
      response: StudioExportResponse,
    }),
    publish: defineRoute({
      id: 'studio.versions.publish',
      method: 'post',
      path: '/studio/bundles/{bundleId}/versions/{version}/publish',
      summary: 'Publish an eval-passed version to the registry (studio.publish; audited)',
      tags: STUDIO,
      request: { params: StudioVersionParams, headers: IdempotencyHeaders, body: StudioPublishBody },
      response: StudioVersionView,
    }),
  },
} as const;

/** The whole HTTP API, grouped. Backend and workbench both read this object. */
// directory (added by 08) -----------------------------------------------------------------------
const DirectoryUserParams = z.object({ userId: z.string().min(1).max(200) });

/** User directory administration (`core-identity-admin` over the bound `UserDirectory` port). */
export const directoryRoutes = {
  list: defineRoute({
    id: 'directory.list',
    method: 'get',
    path: '/admin/directory/users',
    summary: 'List directory users (search by username, name or email)',
    tags: ['directory'],
    roles: OPERATOR,
    request: { query: DirectoryListQuery },
    response: DirectoryListResponse,
  }),
  get: defineRoute({
    id: 'directory.get',
    method: 'get',
    path: '/admin/directory/users/{userId}',
    summary: 'One directory user',
    tags: ['directory'],
    roles: OPERATOR,
    request: { params: DirectoryUserParams },
    response: DirectoryUser,
  }),
  create: defineRoute({
    id: 'directory.create',
    method: 'post',
    path: '/admin/directory/users',
    summary: 'Create a user in the bound directory (temporary password or invite; audited)',
    tags: ['directory'],
    roles: OPERATOR,
    request: { body: CreateDirectoryUserBody },
    response: DirectoryUser,
    successStatus: 201,
  }),
  setRoles: defineRoute({
    id: 'directory.setRoles',
    method: 'put',
    path: '/admin/directory/users/{userId}/roles',
    summary: 'Replace the roles and groups of a user (only an admin may grant admin; audited)',
    tags: ['directory'],
    roles: OPERATOR,
    request: { params: DirectoryUserParams, body: SetDirectoryRolesBody },
    response: DirectoryUser,
  }),
  setEnabled: defineRoute({
    id: 'directory.setEnabled',
    method: 'put',
    path: '/admin/directory/users/{userId}/enabled',
    summary: 'Enable or disable a user; disabling ends their sessions (audited)',
    tags: ['directory'],
    roles: OPERATOR,
    request: { params: DirectoryUserParams, body: SetDirectoryEnabledBody },
    response: DirectoryUser,
  }),
} as const;

export const api = {
  auth: authRoutes,
  tasks: taskRoutes,
  approvals: approvalRoutes,
  registry: registryRoutes,
  modules: moduleRoutes,
  knowledge: knowledgeRoutes,
  ledger: ledgerRoutes,
  audit: auditRoutes,
  admin: adminRoutes,
  demo: demoRoutes,
  metrics: metricsRoutes.scrape,
  directory: directoryRoutes,
  /** 12: agent studio (mounted only when `modules/agent-studio` is enabled). */
  studio: studioRoutes,
  /** 23: model management (`/registry/model-management`). */
  modelManagement: modelManagementRoutes,
} as const;

/** Flat list of every descriptor in `api`. */
export const ALL_ROUTES: readonly AnyRouteDescriptor[] = [
  ...Object.values(authRoutes),
  ...Object.values(taskRoutes),
  ...Object.values(approvalRoutes),
  ...Object.values(registryRoutes),
  ...Object.values(moduleRoutes),
  ...Object.values(knowledgeRoutes),
  ...Object.values(ledgerRoutes),
  ...Object.values(auditRoutes),
  adminRoutes.health,
  adminRoutes.stats,
  ...Object.values(adminRoutes.chaos),
  ...Object.values(adminRoutes.exporter),
  demoRoutes.reset,
  ...Object.values(demoRoutes.scenarios),
  ...Object.values(demoRoutes.runs),
  metricsRoutes.scrape,
  ...Object.values(directoryRoutes),
  studioRoutes.meta,
  ...Object.values(studioRoutes.catalog),
  studioRoutes.templates,
  ...Object.values(studioRoutes.bundles),
  ...Object.values(studioRoutes.versions),
  ...Object.values(modelManagementRoutes),
];
